import { Component, OnInit, AfterViewInit, ElementRef, ViewChild, Input, OnChanges, OnDestroy, inject, EnvironmentInjector, runInInjectionContext, NgZone } from '@angular/core';
import MetisMenu from 'metismenujs';
import { EventService } from '../../core/services/event.service';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { Firestore, collection, query, where, onSnapshot, Unsubscribe } from '@angular/fire/firestore';
import { Auth, authState } from '@angular/fire/auth';
import { OrganizationService } from '../../core/services/organization.service';
import { PermissionsService } from '../../core/services/permissions.service';
import { OrgContextService } from '../../core/services/org-context.service';
import { SubscriptionStateService } from '../../core/services/subscription-state.service';
import { PlatformAdminService } from '../../core/services/platform-admin.service';
import { UserPermissions } from '../../core/models/user-role.model';
import { AnalyticsService } from '../../core/services/analytics.service';
import { filter, firstValueFrom, take } from 'rxjs';

import { HttpClient } from '@angular/common/http';

import { MENU } from './menu';
import { MenuItem } from './menu.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SimplebarAngularModule } from 'simplebar-angular';
import { CommonModule } from '@angular/common';
import Swal from 'sweetalert2';

/**
 * The Echo support widget's loader API, injected by AppComponent.
 *
 * Optional because the widget is not loaded on localhost and its config
 * request can fail, so nothing may ever define it.
 */
declare global {
  interface Window {
    __echoChat?: {
      version: string;
      open: () => void;
      close: () => void;
      toggle: () => void;
      isOpen: () => boolean;
    };
  }
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [SimplebarAngularModule, RouterModule, CommonModule, TranslateModule]
})

/**
 * Sidebar component
 */
export class SidebarComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('componentRef') scrollRef;
  @Input() isCondensed = false;
  menu: any;
  data: any;

  menuItems: MenuItem[] = [];

  hasInvoices: boolean = false;
  private invoicesUnsubscribe?: Unsubscribe;
  private conversationsUnsubscribe?: Unsubscribe;

  /** Total unread incoming messages across all conversations. */
  unreadConversationCount = 0;
  private firestore: Firestore = inject(Firestore);
  private injector = inject(EnvironmentInjector);
  private auth: Auth = inject(Auth);
  private authState$ = authState(this.auth);
  private permissionsService: PermissionsService = inject(PermissionsService);
  private orgContext = inject(OrgContextService);
  private subscriptionState = inject(SubscriptionStateService);
  private ngZone = inject(NgZone);
  private platformAdmin = inject(PlatformAdminService);
  private analytics: AnalyticsService = inject(AnalyticsService);
  private organizationId: string = '';
  userPermissions: UserPermissions | null = null;

  @ViewChild('sideMenu') sideMenu: ElementRef;

  constructor(
    private eventService: EventService,
    private router: Router,
    public translate: TranslateService,
    private http: HttpClient,
    private organizationService: OrganizationService
  ) {
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this._activateMenuDropdown();
        this._scrollElement();
      }
    });
  }

  ngOnInit() {
    this.loadUserPermissions();
    this._scrollElement();
  }

  ngAfterViewInit() {
    this.menu = new MetisMenu(this.sideMenu.nativeElement);
    this._activateMenuDropdown();
  }

  toggleMenu(event) {
    event.currentTarget.nextElementSibling.classList.toggle('mm-show');
  }

  ngOnChanges() {
    if (!this.isCondensed && this.sideMenu || this.isCondensed) {
      setTimeout(() => {
        this.menu = new MetisMenu(this.sideMenu.nativeElement);
      });
    } else if (this.menu) {
      this.menu.dispose();
    }
  }
  _scrollElement() {
    setTimeout(() => {
      if (document.getElementsByClassName("mm-active").length > 0) {
        const currentPosition = document.getElementsByClassName("mm-active")[0]['offsetTop'];
        if (currentPosition > 500)
          if (this.scrollRef.SimpleBar !== null)
            this.scrollRef.SimpleBar.getScrollElement().scrollTop =
              currentPosition + 300;
      }
    }, 300);
  }

  /**
   * remove active and mm-active class
   */
  _removeAllClass(className) {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }

  /**
   * Activate the parent dropdown
   */
  _activateMenuDropdown() {
    this._removeAllClass('mm-active');
    this._removeAllClass('mm-show');
    const links = document.getElementsByClassName('side-nav-link-ref');
    // Both were inferred as null / never[] from their empty initialisers.
    let menuItemEl: Element | null = null;
    // tslint:disable-next-line: prefer-for-of
    const paths: string[] = [];
    for (let i = 0; i < links.length; i++) {
      paths.push(links[i]['pathname']);
    }
    var itemIndex = paths.indexOf(window.location.pathname);
    if (itemIndex === -1) {
      const strIndex = window.location.pathname.lastIndexOf('/');
      const item = window.location.pathname.substr(0, strIndex).toString();
      menuItemEl = links[paths.indexOf(item)];
    } else {
      menuItemEl = links[itemIndex];
    }
    if (menuItemEl) {
      menuItemEl.classList.add('active');
      const parentEl = menuItemEl.parentElement;
      if (parentEl) {
        parentEl.classList.add('mm-active');
        const parent2El = parentEl.parentElement?.closest('ul');
        if (parent2El && parent2El.id !== 'side-menu') {
          parent2El.classList.add('mm-show');
          const parent3El = parent2El.parentElement;
          if (parent3El && parent3El.id !== 'side-menu') {
            parent3El.classList.add('mm-active');
            const childAnchor = parent3El.querySelector('.has-arrow');
            const childDropdown = parent3El.querySelector('.has-dropdown');
            if (childAnchor) { childAnchor.classList.add('mm-active'); }
            if (childDropdown) { childDropdown.classList.add('mm-active'); }
            const parent4El = parent3El.parentElement;
            if (parent4El && parent4El.id !== 'side-menu') {
              parent4El.classList.add('mm-show');
              const parent5El = parent4El.parentElement;
              if (parent5El && parent5El.id !== 'side-menu') {
                parent5El.classList.add('mm-active');
                const childanchor = parent5El.querySelector('.is-parent');
                if (childanchor && parent5El.id !== 'side-menu') { childanchor.classList.add('mm-active'); }
              }
            }
          }
        }
      }
    }

  }

  /**
   * Load user permissions and initialize menu
   */
  async loadUserPermissions(): Promise<void> {
    const user = this.auth.currentUser;
    if (user) {
      this.userPermissions = await this.permissionsService.getUserPermissions(user.uid);
      // Resolve the organisation type before building the menu, so PCO-only
      // items are never briefly visible to a standalone organisation. Platform
      // admin state is resolved for the same reason — a Solenex administrator
      // must not see a flash of the customer menu on the way to the console.
      //
      // The subscription is waited on for the opposite reason: plan-gated items
      // read `limits`, which arrives from a Firestore listener and is empty
      // until its first emission. Building the menu before then hides Website
      // Chat from an organisation that pays for it, and nothing rebuilds the
      // menu afterwards — so it would stay hidden until the next full reload.
      await Promise.all([
        this.orgContext.load(),
        this.platformAdmin.loadState(),
        firstValueFrom(
          this.subscriptionState.state$.pipe(filter((state) => state.initialized), take(1))
        ).catch(() => null)
      ]);
      this.initialize();

      // Load organization for invoice check
      this.organizationService.getUserProfile(user.uid).subscribe({
        next: (profile) => {
          if (profile?.organizationId) {
            this.organizationId = profile.organizationId;
            this.checkInvoices();
            this.watchUnreadConversations();
          }
        },
        error: () => {
          // Silently fail - menu already initialized with permissions
        }
      });
    }
  }

  /**
   * Initialize
   */
  initialize(): void {
    // The console has its own site (admin.echo.solenex.it) and its own
    // navigation, so this menu no longer has an administrator variant to switch
    // to — there is only the customer menu here now.
    const hasPco = this.orgContext.hasPco();
    // loadState() has already run in ngOnInit's Promise.all, so this signal is
    // populated by the time the menu is built.
    const inSupportSession = this.platformAdmin.impersonation() !== null;

    this.menuItems = MENU.filter(item => {
      // Support Staff (the role, not a Solenex support session) only have
      // the Conversations page — see showToSupportStaff in menu.model.ts.
      if (this.userPermissions?.isConversationsOnly) {
        return item.showToSupportStaff === true;
      }

      // Planning Center-only items (Automations) for orgs that have it.
      if (item.requiresPco && !hasPco) {
        return false;
      }

      // Pages a Solenex support session has no business opening — see
      // hideInSupportMode in menu.model.ts. The routes are guarded too; this
      // just keeps them off the screen.
      if (item.hideInSupportMode && inSupportSession) {
        return false;
      }

      // Hide Organization menu from viewers UNLESS they are billing admins
      if (item.label === 'Organization' && this.userPermissions?.role === 'viewer') {
        // Show to viewers who are billing admins (they can see subscription/invoices)
        return this.userPermissions?.isBillingAdministrator === true;
      }

      // Hide Create Campaign menu from viewers
      if (item.label === 'Create Campaign' && !this.userPermissions?.canSendMessages) {
        return false;
      }

      return true;
    }).map(item => {
      if (item.label === 'Organization' && item.subItems) {
        return {
          ...item,
          subItems: item.subItems.filter((subItem: MenuItem) => {
            // Billing changes are real and irreversible; keep them out of a
            // support session. Checked first so it wins over the role-based
            // branches below.
            if (subItem.hideInSupportMode && inSupportSession) {
              return false;
            }

            // Filter Invoices - only billing admins or owners
            if (subItem.label === 'Invoices') {
              return this.userPermissions?.canViewInvoices && this.hasInvoices;
            }

            // Filter Subscription - only billing admins or owners
            if (subItem.label === 'Subscription') {
              return this.userPermissions?.canManageSubscriptions;
            }

            // Filter Users - only administrators and owners
            if (subItem.label === 'Users') {
              return this.userPermissions?.canManageUsers;
            }

            // Filter Settings - only administrators and owners can view
            // (WhatsApp/Webhook linking restricted to owner in the component itself)
            if (subItem.label === 'Settings') {
              return this.userPermissions?.canViewSettings;
            }

            // Filter Messaging Pricing - same as Settings
            if (subItem.label === 'Messaging Pricing') {
              return this.userPermissions?.canViewSettings;
            }

            // Filter API Access - owner only. Without this branch the item
            // would fall through to the default below and show to everyone.
            if (subItem.label === 'API Access') {
              return this.userPermissions?.canManageApiApps === true;
            }

            // Filter Website Chat - administrators and owners. No longer
            // plan-gated here: every org sees the page, but it renders
            // read-only unless the plan includes web chat (or is on trial) —
            // see widget-settings.component.ts.
            if (subItem.label === 'Website Chat') {
              return this.userPermissions?.canManageWidgets === true;
            }

            // Filter SMS - owner only, same trust class as API Access. The
            // page renders read-only when the plan lacks SMS.
            if (subItem.label === 'SMS') {
              return this.userPermissions?.canManageSms === true;
            }

            return true;
          })
        };
      }
      return item;
    }).filter(item => {
      // Remove Organization menu if it has no visible subitems
      if (item.label === 'Organization' && item.subItems) {
        const hasVisibleSubitems = item.subItems.length > 0;
        console.log('Organization menu subitems after filtering:', item.subItems, 'Visible:', hasVisibleSubitems);
        return hasVisibleSubitems;
      }
      return true;
    });

    console.log('Final menu items:', this.menuItems);
    console.log('User permissions:', this.userPermissions);

    // Re-activate menu dropdown after Angular renders the new menu items
    setTimeout(() => {
      this._activateMenuDropdown();
    });
  }

  /**
   * Load organization and check for invoices
   * @deprecated Use loadUserPermissionsAndInitialize instead
   */
  async loadOrganizationAndCheckInvoices(): Promise<void> {
    const user = await firstValueFrom(this.authState$);
    if (user) {
      this.organizationService.getUserProfile(user.uid).subscribe({
        next: (profile) => {
          if (profile && profile.organizationId) {
            this.organizationId = profile.organizationId;
            this.checkInvoices();
            this.watchUnreadConversations();
            // Reinitialize menu with user role
            this.initialize();
          }
        },
        error: (error) => {
          console.error('Error loading user profile:', error);
        }
      });
    }
  }

  /**
   * Check if organization has invoices
   */
  checkInvoices(): void {
    if (!this.organizationId) {
      return;
    }

    const invoicesRef = collection(this.firestore, `organizations/${this.organizationId}/invoices`);
    const invoicesQuery = query(invoicesRef);

    // Set up real-time listener for invoices
    this.invoicesUnsubscribe = runInInjectionContext(this.injector, () => onSnapshot(invoicesQuery,
      (querySnapshot) => {
        const previousHasInvoices = this.hasInvoices;
        const hasInvoices = !querySnapshot.empty;

        // Also out of zone — rebuilding the menu here without re-entering it
        // leaves the new item list unrendered. See watchUnreadConversations.
        this.ngZone.run(() => {
          this.hasInvoices = hasInvoices;

          // Reinitialize menu if invoice availability changed
          if (previousHasInvoices !== hasInvoices) {
            this.initialize();
          }
        });
      },
      (error) => {
        console.error('Error checking invoices:', error);
      }
    ));
  }

  /**
   * Keep a live count of unread incoming messages for the Conversations badge.
   *
   * Filtered to conversations that actually have unread messages rather than
   * reading the whole collection. Conversation documents are small now that
   * messages live in a subcollection, so this is no longer load-bearing — but
   * a badge only needs the unread ones, and the filter keeps the listener from
   * waking on every conversation an organisation touches.
   */
  watchUnreadConversations(): void {
    if (!this.organizationId) {
      return;
    }

    // Replace any listener left over from a previous organisation.
    this.conversationsUnsubscribe?.();

    const conversationsRef = collection(
      this.firestore,
      `organizations/${this.organizationId}/whatsappConversations`
    );
    const unreadQuery = query(conversationsRef, where('unreadCount', '>', 0));

    this.conversationsUnsubscribe = runInInjectionContext(this.injector, () => onSnapshot(
      unreadQuery,
      (snapshot) => {
        const total = snapshot.docs.reduce(
          (sum, docSnap) => sum + (docSnap.data()['unreadCount'] || 0),
          0
        );

        // Firestore's modular SDK delivers snapshots OUTSIDE Angular's zone, so
        // assigning here alone updates the field but never repaints the badge —
        // it would sit stale until something unrelated happened to trigger
        // change detection. Same pattern as conversation.service.ts.
        this.ngZone.run(() => {
          this.unreadConversationCount = total;
        });
      },
      (error) => {
        console.error('Error watching unread conversations:', error);
        this.ngZone.run(() => {
          this.unreadConversationCount = 0;
        });
      }
    ));
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    if (this.invoicesUnsubscribe) {
      this.invoicesUnsubscribe();
    }
    if (this.conversationsUnsubscribe) {
      this.conversationsUnsubscribe();
    }
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }

  /**
   * Open the Echo support widget for feedback.
   *
   * Feedback used to open a Microsoft Forms Customer Voice survey through a
   * `renderSurvey` global loaded in index.html. It now goes to the same chat
   * widget as support, so feedback lands in a real conversation someone can
   * reply to rather than in a form nobody watches.
   */
  async openFeedbackForm(): Promise<void> {
    this.analytics.logEvent('support_requested', {
      support_type: 'feedback',
      source: 'sidebar_button'
    });

    const chat = window.__echoChat;
    if (chat) {
      chat.open();
      return;
    }

    // The widget is not on the page — on localhost, or its config request
    // failed. Say so rather than leaving the button doing nothing.
    await Swal.fire({
      icon: 'info',
      title: 'Chat Unavailable',
      text: 'The support chat could not be loaded. Please email support@solenex.it instead.',
      confirmButtonColor: '#556ee6'
    });
  }
}
